import { Component, OnInit } from '@angular/core';
import { PetServices } from '../../../../services/petservices';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { DeviceDetectorService } from '../../../../services/device-detector.service';
import { MatTableDataSource } from '@angular/material/table';
import { UserService } from '../../../../services/user';

@Component({
  selector: 'app-clientsbookings',
  templateUrl: './clientsbookings.component.html',
  styleUrl: './clientsbookings.component.scss'
})
export class ClientsbookingsComponent implements OnInit {

  contentReady = false;
  noData = false;

  allData: any[] = [];
  dataSource = new MatTableDataSource<any>([]);

  displayedColumns: string[] = [
    'id',
    'client',
    'phone',
    'service',
    'pet',
    'booking_date',
    'status',
    'deferred_booking_date',
    'website',
    'actions'
  ];

  total = 0;
  page = 1;
  perPage = 10;

  warehouseId: number | null = null;

  showDeferredPopup = false;
  selectedBooking: any = null;
  deferredBookingDate = '';
  isUpdatingStatus = false;

  todayDateTime = this.formatDateTimeForInput(new Date());

  isMobile!: boolean;
  isAr = false;
  isSubAdmin = false;

  searchKey = '';
  searched = false;

  bookingRoute = '/AddBooking';

  statusMap: any = {
    0: { label: 'pending', class: 'pending' },
    1: { label: 'approved', class: 'approved' },
    2: { label: 'rejected', class: 'rejected' },
    3: { label: 'deferred', class: 'deferred' },
    4: { label: 'completed', class: 'completed' },
  };

  petTypeMap: any = {
    cat: 'cats',
    cats: 'cats',
    dog: 'dogs',
    dogs: 'dogs',
    bird: 'birds',
    birds: 'birds',
    other: 'other',

    0: 'cats',
    1: 'dogs',
    2: 'birds',
    3: 'other',
  };

  constructor(
    private _petServices: PetServices,
    private router: Router,
    private route: ActivatedRoute,
    private deviceService: DeviceDetectorService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.setLanguage();
    this.loadUserRole();
    this.setWarehouseIdFromLocalStorage();

    this.isMobile = this.deviceService.isMobileDevice();

    if (this.isMobile) {
      this.router.navigate(['/clientsbookings.mobile'], {
        queryParams: this.warehouseId ? { warehouse_id: this.warehouseId } : {}
      });
      return;
    }

    this.getBookings();
  }

  private loadUserRole(): void {
    this.userService.getAccountType().subscribe({
      next: (data) => {
        this.isSubAdmin = String(data?.user_type) === '6';
      },
      error: () => {
        this.isSubAdmin = false;
      }
    });
  }

  private setLanguage(): void {
    try {
      const lang =
        localStorage.getItem('lang') ||
        localStorage.getItem('language') ||
        localStorage.getItem('currentLang') ||
        '';

      this.isAr = lang.toLowerCase().startsWith('ar');
    } catch {
      this.isAr = false;
    }
  }

private setWarehouseIdFromLocalStorage(): void {
  try {
    const posId = localStorage.getItem('posId');

    this.warehouseId = posId ? Number(posId) : null;

    if (!this.warehouseId || isNaN(this.warehouseId)) {
      this.warehouseId = null;
    }
  } catch {
    this.warehouseId = null;
  }
}



  getBookings(): void {
    this.contentReady = true;

    const phone = this.searchKey.trim();

    const request$ = this.searched && phone
      ? this._petServices.searchBookingsByPhone(
          phone,
          this.page,
          this.perPage,
          this.warehouseId
        )
      : this._petServices.getAllBookings(
          this.page,
          this.perPage,
          this.warehouseId
        );

    request$.subscribe({
      next: (res: any) => {
        const list = Array.isArray(res?.data) ? res.data : [];

        this.allData = list;
        this.dataSource.data = list;

        this.total = Number(res?.total || list.length || 0);
        this.noData = list.length === 0;

        this.contentReady = false;
      },
      error: (err) => {
        console.error(err);

        this.allData = [];
        this.dataSource.data = [];
        this.total = 0;
        this.noData = true;
        this.contentReady = false;
      }
    });
  }

  search(): void {
    const phone = this.searchKey.trim();

    if (!phone) {
      this.clearSearch();
      return;
    }

    this.searched = true;
    this.page = 1;
    this.getBookings();
  }

  clearSearch(): void {
    this.searchKey = '';
    this.searched = false;
    this.page = 1;
    this.getBookings();
  }

  onEnterPressed(): void {
    this.search();
  }

  pageChanged(event: PageEvent): void {
    this.page = event.pageIndex + 1;
    this.perPage = event.pageSize;
    this.getBookings();
  }

  getClientName(booking: any): string {
    return (
      booking?.client_name ||
      booking?.user?.name ||
      booking?.user?.first_name ||
      booking?.user?.last_name ||
      '—'
    );
  }

  getPhone(booking: any): string {
    return (
      booking?.phone_number ||
      booking?.phone ||
      booking?.user?.phone_number ||
      booking?.user?.phone ||
      '—'
    );
  }

  getBookingServiceName(booking: any): string {
    const service = booking?.pet_service || booking?.petService || booking?.service;

    if (!service) return '—';

    if (this.isAr) {
      return service?.name_ar || service?.name_en || service?.name || '—';
    }

    return service?.name_en || service?.name_ar || service?.name || '—';
  }

  getPetTypeLabel(petType: any): string {
    if (petType === null || petType === undefined || petType === '') {
      return 'other';
    }

    const key = String(petType).trim().toLowerCase();

    return this.petTypeMap[key] || 'other';
  }

  getStatusLabel(status: any): string {
    return this.statusMap[Number(status)]?.label || 'pending';
  }

  getStatusClass(status: any): string {
    return this.statusMap[Number(status)]?.class || 'pending';
  }

  isStatus(currentStatus: any, targetStatus: number): boolean {
    return Number(currentStatus) === Number(targetStatus);
  }

  shouldShowStatusAction(currentStatus: any, targetStatus: number): boolean {
    return !this.isFinalStatus(currentStatus) && Number(currentStatus) !== Number(targetStatus);
  }

  isFinalStatus(status: any): boolean {
    return [2, 4].includes(Number(status));
  }

  hasBookingActions(booking: any): boolean {
    return !this.isFinalStatus(booking?.status) || !this.isSubAdmin;
  }

  setBookingStatus(booking: any, status: number): void {
    if (!booking?.id) return;

    if (this.isFinalStatus(booking?.status)) {
      return;
    }

    if (status === 3) {
      this.openDeferredDatePopup(booking);
      return;
    }

    const oldStatus = booking.status;
    const oldDeferredDate = booking.deferred_booking_date;

    booking.status = status;
    booking.deferred_booking_date = null;

    const payload: any = {
      status: Number(status)
    };

    this.isUpdatingStatus = true;

    this._petServices.updateBookingStatus(booking.id, payload).subscribe({
      next: (res: any) => {
        booking.status = Number(res?.data?.status ?? status);
        booking.deferred_booking_date = res?.data?.deferred_booking_date ?? null;

        this.isUpdatingStatus = false;
      },
      error: (err) => {
        console.error(err);

        booking.status = oldStatus;
        booking.deferred_booking_date = oldDeferredDate;

        this.isUpdatingStatus = false;
        alert(err?.error?.message || 'Failed to update booking status');
      }
    });
  }

  openDeferredDatePopup(booking: any): void {
    if (!booking?.id) return;

    if (this.isFinalStatus(booking?.status)) {
      return;
    }

    this.selectedBooking = booking;

    this.deferredBookingDate = this.formatDateTimeForDateTimeLocal(
      booking.deferred_booking_date || booking.booking_date || new Date()
    );

    this.showDeferredPopup = true;
  }

  closeDeferredPopup(): void {
    if (this.isUpdatingStatus) return;

    this.showDeferredPopup = false;
    this.selectedBooking = null;
    this.deferredBookingDate = '';
  }

  submitDeferredDate(): void {
    if (!this.selectedBooking) return;

    if (!this.deferredBookingDate) {
      alert('Please select deferred date and time');
      return;
    }

    const booking = this.selectedBooking;

    const oldStatus = booking.status;
    const oldDeferredDate = booking.deferred_booking_date;

    const cleanDateTime = this.convertDateTimeLocalToMysql(this.deferredBookingDate);

    const payload: any = {
      status: 3,
      deferred_booking_date: cleanDateTime
    };

    booking.status = 3;
    booking.deferred_booking_date = cleanDateTime;

    this.isUpdatingStatus = true;

    this._petServices.updateBookingStatus(booking.id, payload).subscribe({
      next: (res: any) => {
        booking.status = Number(res?.data?.status ?? 3);
        booking.deferred_booking_date =
          res?.data?.deferred_booking_date ?? cleanDateTime;

        this.isUpdatingStatus = false;
        this.closeDeferredPopup();
      },
      error: (err) => {
        console.error(err);

        booking.status = oldStatus;
        booking.deferred_booking_date = oldDeferredDate;

        this.isUpdatingStatus = false;
        alert(err?.error?.message || 'Failed to update deferred date');
      }
    });
  }

  formatDateTimeForInput(dateValue: any): string {
    const date = new Date(dateValue);

    if (isNaN(date.getTime())) {
      return this.formatDateTimeForInput(new Date());
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  formatDateTimeForDateTimeLocal(value: any): string {
    if (!value) {
      return this.formatDateTimeForInput(new Date());
    }

    let cleanValue = String(value)
      .replace('T', ' ')
      .replace('Z', '')
      .split('.')[0]
      .trim();

    const mysqlMatch = cleanValue.match(
      /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/
    );

    if (mysqlMatch) {
      return `${mysqlMatch[1]}-${mysqlMatch[2]}-${mysqlMatch[3]}T${mysqlMatch[4]}:${mysqlMatch[5]}`;
    }

    const usMatch = cleanValue.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i
    );

    if (usMatch) {
      const month = usMatch[1].padStart(2, '0');
      const day = usMatch[2].padStart(2, '0');
      const year = usMatch[3];

      let hours = Number(usMatch[4]);
      const minutes = usMatch[5];
      const ampm = usMatch[6].toUpperCase();

      if (ampm === 'PM' && hours < 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;

      return `${year}-${month}-${day}T${String(hours).padStart(2, '0')}:${minutes}`;
    }

    return this.formatDateTimeForInput(new Date());
  }

  convertDateTimeLocalToMysql(dateTimeLocal: string): string {
    if (!dateTimeLocal) return '';

    const normalized = dateTimeLocal.replace('T', ' ');

    if (normalized.length === 16) {
      return `${normalized}:00`;
    }

    return normalized;
  }

  formatDateTimeView(value: any): string {
    if (!value) return '—';

    let cleanValue = String(value)
      .replace('T', ' ')
      .replace('Z', '')
      .split('.')[0]
      .trim();

    const parts = cleanValue.split(' ');
    const datePart = parts[0];
    const timePart = parts[1];

    if (!datePart || !timePart) return cleanValue;

    const [hoursRaw, minutes] = timePart.split(':');
    let hours = Number(hoursRaw);

    if (isNaN(hours)) return cleanValue;

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    const formattedHours = String(hours).padStart(2, '0');

    return `${datePart} ${formattedHours}:${minutes} ${ampm}`;
  }

  deleteBooking(id: number): void {
    if (this.isSubAdmin) return;
    if (!id) return;

    if (!confirm('Are you sure you want to delete this booking?')) return;

    this._petServices.deleteBooking(id).subscribe({
      next: () => {
        if (this.allData.length === 1 && this.page > 1) {
          this.page -= 1;
        }

        this.getBookings();
      },
      error: (err) => {
        console.error(err);
        alert(err?.error?.message || 'Failed to delete booking');
      }
    });
  }
}
